import { useLazyQuery } from "@apollo/client"
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import {
  listFootprintQuestions,
  listMetrics,
} from "../services/graphql/queries"
import { logger, Status } from "../services/utilities/utility"
import { MainContext } from "./main"
import FootprintQuestion from "../models/footprintQuestion"

interface FootprintQuestionsContextInterface {
  footprintQuestionsLoading: boolean
  footprintQuestionsList: FootprintQuestion[]
}

const FootprintQuestionsContext =
  createContext<FootprintQuestionsContextInterface>({
    footprintQuestionsLoading: true,
    footprintQuestionsList: [],
  })

const FootprintQuestionsController = ({
  children,
}: {
  children: ReactNode
}) => {
  const { setError, setErrorMessage, updatePanelStatus } =
    useContext(MainContext)

  // loadings
  const [footprintQuestionsLoading, setMetricsLoading] = useState<boolean>(true) // loading for footprint questions

  // states
  const [footprintQuestionsList, setMetricsList] = useState<
    FootprintQuestion[]
  >([]) // all footprint questions list

  // queries
  const [listFootprintQuestionsQuery] = useLazyQuery(listFootprintQuestions)

  // get footprint questions list
  const getFootprintQuestionsList = useCallback(async () => {
    updatePanelStatus("Footprint Questions", { loading: true })

    try {
      logger(Status.Api, "QUERY footprintQuestionsList", listMetrics)
      const { data } = await listFootprintQuestionsQuery()
      logger(
        Status.Info,
        "footprint questions list",
        data.footprintQuestionsList.items
      )

      setMetricsList(data.footprintQuestionsList.items)

      setMetricsLoading(false)
      updatePanelStatus("Footprint Questions", {
        status: "success",
        loading: false,
      })
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(true)
        setErrorMessage(e.message)
        logger(Status.Error, `footprintQuestionsList`, e.message)
      }
      setMetricsLoading(false)
      updatePanelStatus("Footprint Questions", {
        status: "error",
        loading: false,
      })
    }
  }, [setError, setErrorMessage])

  // add update function to panel status
  useEffect(() => {
    updatePanelStatus("Footprint Questions", {
      updateFunction: getFootprintQuestionsList,
    })
  }, [])

  // initial fetch
  useEffect(() => {
    getFootprintQuestionsList()
  }, [])

  return (
    <FootprintQuestionsContext.Provider
      value={{
        footprintQuestionsLoading,
        footprintQuestionsList,
      }}
    >
      {children}
    </FootprintQuestionsContext.Provider>
  )
}

export { FootprintQuestionsController, FootprintQuestionsContext }
